/**
 * earlyIntel.js — Early Intel domain service.
 * Summary, exposures, notifications, settings.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
  normalizeApiList,
  isEndpointUnavailable,
  EARLY_INTEL_ENDPOINT_UNAVAILABLE_KEY,
  EARLY_INTEL_EXPOSURES_ENDPOINT_UNAVAILABLE_KEY,
} from './_core';

export async function getEarlyIntelSummary(token = null, query = null) {
  if (isEndpointUnavailable(EARLY_INTEL_ENDPOINT_UNAVAILABLE_KEY)) return null;
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(toApiUrl(`/early-intel/${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Early intel fetch failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Early intel summary');
}

export async function getEarlyIntelExposures(token = null, query = null) {
  if (isEndpointUnavailable(EARLY_INTEL_EXPOSURES_ENDPOINT_UNAVAILABLE_KEY)) return [];
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(toApiUrl(`/early-intel/exposures/${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Early intel exposures fetch failed (${response.status})`);
    throw new Error(msg);
  }
  return normalizeApiList(await parseJsonOrThrow(response, 'Early intel exposures'));
}

export async function markEarlyIntelExposureNotified(exposureUuid, token = null) {
  const uuid = String(exposureUuid || '').trim();
  if (!uuid) throw new Error('Exposure UUID is required.');

  try {
    const response = await fetchWithAuthRetry(toApiUrl(`/early-intel/exposures/${encodeURIComponent(uuid)}/mark-notified/`), {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify({}),
    }, token);

    if (!response.ok) {
      const msg = await readApiErrorMessage(response, `Mark notified failed (${response.status})`);
      throw new Error(msg);
    }

    return parseJsonOrThrow(response, 'Mark exposure notified');
  } catch (err) {
    if (err.message.includes('404') || err.message.includes('HTML')) {
      throw new Error('Notification feature is not yet available.');
    }
    throw err;
  }
}

export async function getEarlyIntelFeatureSettings(token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/early-intel/settings/'),
    jsonFetchOptions(token),
    token
  );
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Early intel settings fetch failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Early intel feature settings');
}

export async function updateEarlyIntelFeatureSettings(payload, token = null) {
  const p = payload || {};
  const body = {};
  if ('featureEnabled' in p) body.featureEnabled = Boolean(p.featureEnabled);
  if ('maxExposuresPerMonth' in p) {
    const v = Number(p.maxExposuresPerMonth);
    body.maxExposuresPerMonth = Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;
  }
  if ('notificationsEnabled' in p) body.notificationsEnabled = Boolean(p.notificationsEnabled);
  if ('notificationFrequency' in p) {
    body.notificationFrequency = String(p.notificationFrequency || 'daily').trim().toLowerCase();
  }

  const response = await fetchWithAuthRetry(
    toApiUrl('/early-intel/settings/'),
    {
      method: 'PATCH',
      ...jsonFetchOptions(token),
      body: JSON.stringify(body),
    },
    token
  );
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Early intel settings update failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Update early intel feature settings');
}

export async function resetEarlyIntelFeatureSettingsMonthlyCounter(token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/early-intel/settings/reset-monthly-counter/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify({}),
    },
    token
  );
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Early intel monthly reset failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Reset early intel feature settings monthly counter');
}
